import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Pager } from "@/app/Pager";
import { Fragment, useState } from "react";
import { audit, AUDIT_ACTION } from "@/app/audit";
import { useIsSuperAdmin } from "@/app/auth";
import { ClearFilters } from "@/app/ClearFilters";
import { loadingOf } from "@/app/queries";
import { ReadonlyBlock } from "@/app/ReadonlyBlock";
import { totalOf, useCursorPage } from "@/app/usePage";
import { Alert, Button, EmptyState, FILTER_W, formatTime, Grid, Input, PageHeader, Select, Table, TableWrap, Th, Toolbar } from "@/ui";

/** 审计里出现的实体类型（表名），供过滤；docs/03 7.2。 */
const ENTITY_TYPES = ["customer", "supplier", "account", "channel", "numbering_plan", "sell_price", "cost_price", "currency_rate", "route", "channel_group", "policy_binding", "price_correction"];

/** 审计日志：最近的记录，按实体类型与操作者过滤、按页向后端取，展开看改动前后的整行（docs/04 第 8 节）。只有超级管理员能看（D181）。 */
export function Audit() {
  const allowed = useIsSuperAdmin(); // 其他角色从地址栏直接进来时：服务端 403，这里只给说明、不发请求
  const [entityType, setEntityType] = useState("");
  const [actor, setActor] = useState("");
  const [expanded, setExpanded] = useState<number | null>(null);
  const pager = useCursorPage<number>("audit", [entityType, actor]);
  const list = useQuery({
    queryKey: ["audit", entityType, actor, pager.limit, pager.cursor],
    queryFn: () => audit.list({ entity_type: entityType, actor: actor.trim(), limit: pager.limit, before_id: pager.cursor }),
    placeholderData: keepPreviousData, // 翻页时留着上一页，不闪"加载中"
    enabled: allowed,
  });
  const total = useQuery({ queryKey: ["audit", "count", entityType, actor], queryFn: () => audit.count({ entity_type: entityType, actor: actor.trim() }), enabled: allowed }); // 总数（决策 D182）
  const paged = { ...pager.view(list.data ?? [], (r) => r.id), ...totalOf(total.data) };
  const rows = paged.rows;
  const header = <PageHeader title="审计日志" description="主数据增改、发布、充值与价格更正的操作记录，详情里是改动前后的整行。" />;
  if (!allowed) return <>{header}<Alert title="只有超级管理员能查看审计日志" /></>;
  return (
    <>
      {header}
      {list.error && <Alert tone="danger" title="加载失败">{list.error.message}</Alert>}
      <Toolbar actions={(entityType || actor) && <ClearFilters onClear={() => { setEntityType(""); setActor(""); }} />}>
        <Select size="sm" width={FILTER_W.select} ariaLabel="实体类型" options={[{ value: "", label: "全部实体" }, ...ENTITY_TYPES.map((t) => ({ value: t, label: t }))]} value={entityType} onChange={setEntityType} />
        <Input size="sm" placeholder="操作者" aria-label="操作者" style={{ width: FILTER_W.select }} value={actor} onChange={(e) => setActor(e.target.value)} />
      </Toolbar>
      <TableWrap loading={loadingOf(list)}>
        {rows.length ? (
          <>
          <Table dense>
            <thead><tr><Th w="time">时间</Th><Th w={160}>操作者</Th><Th w={120}>动作</Th><Th w={160}>实体</Th><Th w={120}>标识</Th><Th w={160}>来源 IP</Th><Th /></tr></thead>
            <tbody>
              {rows.map((r) => (
                <Fragment key={r.id}>
                  <tr>
                    <td className="ax-mono">{formatTime(r.created_at)}</td><td>{r.actor}</td><td>{AUDIT_ACTION[r.action] ?? r.action}</td>
                    <td className="ax-mono">{r.entity_type}</td><td className="ax-mono">{r.entity_id}</td><td className="ax-mono">{r.remote_ip}</td>
                    <td><Button variant="ghost" size="sm" onClick={() => setExpanded(expanded === r.id ? null : r.id)}>{expanded === r.id ? "收起" : "详情"}</Button></td>
                  </tr>
                  {expanded === r.id && (
                    <tr>
                      <td colSpan={7}>
                        {/* 两个框拉成等高，对照时上下对齐；Grid 默认顶部对齐是给表单字段的 */}
                        <Grid columns={2} style={{ alignItems: "stretch" }}>
                          <ReadonlyBlock label="改动前" value={r.before} />
                          <ReadonlyBlock label="改动后" value={r.after} />
                        </Grid>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </Table>
          <Pager {...paged} />
          </>
        ) : (
          !list.isPending && <EmptyState title="没有记录" />
        )}
      </TableWrap>
    </>
  );
}
